import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import NewsCard from "@/components/news/NewsCard";
import { GridSkeleton, HeroSkeleton } from "@/components/news/Skeletons";
import { TID } from "@/lib/testIds";
import { Button } from "@/components/ui/button";
import { useSEO } from "@/lib/useSEO";

const PAGE_SIZE = 12;

export default function CategoryPage() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10));

  const [category, setCategory] = useState(null);
  const [seo, setSeo] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, loading: true, error: null });

  useEffect(() => {
    // Special "ultimas-noticias" acts as global latest feed
    setSeo(null);
    if (slug === "ultimas-noticias") {
      setCategory({ name: "Últimas noticias", slug, description: "Toda la actualidad al minuto de Tierra del Fuego." });
    } else {
      api
        .category(slug)
        .then(setCategory)
        .catch(() => setCategory(null));
      api.categorySEO(slug).then(setSeo).catch(() => {});
    }
  }, [slug]);

  useSEO(seo ? {
    title: seo.title,
    description: seo.description,
    canonical: seo.canonical,
    image: seo.image,
    og: seo.og,
    twitter: seo.twitter,
    jsonLd: seo.json_ld,
  } : {});

  useEffect(() => {
    setData((d) => ({ ...d, loading: true, error: null }));
    api
      .articles({
        category: slug,
        limit: PAGE_SIZE,
        skip: (page - 1) * PAGE_SIZE,
        sort: "published_at",
      })
      .then((r) => setData({ items: r.items || [], total: r.total || 0, loading: false, error: null }))
      .catch((e) => setData({ items: [], total: 0, loading: false, error: e }));
  }, [slug, page]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const feature = data.items[0];
  const grid = data.items.slice(1);

  const goPage = (n) => {
    const next = Math.min(Math.max(1, n), totalPages);
    setSearchParams({ page: String(next) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid={TID.categoryPage} className="container-wide py-8">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-turquoise font-semibold mb-2">Sección</div>
        <h1 data-testid={TID.categoryTitle} className="font-serif font-black text-4xl sm:text-5xl text-blueblack">
          {category?.name || (data.loading ? "…" : "Sección")}
        </h1>
        {category?.description && (
          <p className="mt-3 text-textgray text-base leading-relaxed max-w-2xl">{category.description}</p>
        )}
        <div className="mt-6 h-[3px] w-16 bg-watergreen" />
      </div>

      {data.loading && page === 1 ? (
        <>
          <HeroSkeleton />
          <div className="mt-10">
            <GridSkeleton count={8} columns={4} />
          </div>
        </>
      ) : data.items.length === 0 ? (
        <div className="border border-softblue bg-white p-10 text-center">
          <p className="text-textgray">Todavía no hay noticias publicadas en esta sección.</p>
          <Link to="/" className="mt-4 inline-block text-petroleum hover:text-turquoise font-semibold uppercase text-xs tracking-widest">
            Volver a la portada
          </Link>
        </div>
      ) : (
        <>
          {feature && page === 1 && (
            <section className="mb-12">
              <NewsCard article={feature} variant="large" />
            </section>
          )}

          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {(page === 1 ? grid : data.items).map((a) => (
              <NewsCard key={a.id} article={a} variant="default" />
            ))}
          </section>

          {totalPages > 1 && (
            <div className="mt-12 flex items-center justify-center gap-3">
              <Button
                data-testid={TID.categoryPagePrev}
                variant="outline"
                onClick={() => goPage(page - 1)}
                disabled={page <= 1}
                className="rounded-none border-softblue text-nightblue hover:bg-lightgray"
              >
                ← Anterior
              </Button>
              <span className="text-xs uppercase tracking-widest text-textgray font-semibold">
                Página {page} de {totalPages}
              </span>
              <Button
                data-testid={TID.categoryPageNext}
                variant="outline"
                onClick={() => goPage(page + 1)}
                disabled={page >= totalPages}
                className="rounded-none border-softblue text-nightblue hover:bg-lightgray"
              >
                Siguiente →
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
